import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, MapPin } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, CardContent, CardHeader } from '@/components/ui';
import { gisApi, type BatchGeocodeResult } from '@/lib/api';

/**
 * Studio surface for the `/api/v1/gis/geocode-missing` endpoint.
 *
 * Shows how many placeable entities are still missing coordinates,
 * with a small sample preview, and runs the batch on demand. ETA is
 * shown up-front because Nominatim's 1 req/s limit makes 50 entities
 * take ≈1 minute — long enough that we should warn the analyst before
 * they click.
 */
export function GISBatchGeocode() {
  const { t } = useTranslation(['studio', 'common']);
  const qc = useQueryClient();
  const [batchLimit, setBatchLimit] = useState(50);
  const [minConfidence, setMinConfidence] = useState(0.4);
  const [result, setResult] = useState<BatchGeocodeResult | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['studio', 'gis', 'geocode-candidates'],
    queryFn: () => gisApi.geocodeCandidates(8),
    staleTime: 30_000,
  });

  const run = useMutation({
    mutationFn: () => gisApi.geocodeMissing(batchLimit, minConfidence),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ['studio', 'gis', 'geocode-candidates'] });
      // Tip the map cache too, so the markers refresh immediately
      qc.invalidateQueries({ queryKey: ['map', 'entities'] });
    },
  });

  const total = data?.total ?? 0;
  const planned = Math.min(batchLimit, total);
  const etaSec = planned > 0 ? Math.ceil(planned * 1.1) : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-slate-500" aria-hidden />
          <h2 className="text-h2 text-brand-900">{t('studio:gis.batchGeocode.title')}</h2>
        </div>
        <p className="mt-1 text-small text-slate-600">
          {t('studio:gis.batchGeocode.subtitle')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && (
          <p className="text-small text-slate-500">{t('common:status.loading')}</p>
        )}

        {!isLoading && data && (
          <>
            {total === 0 ? (
              <p className="text-body text-slate-500">
                {t('studio:gis.batchGeocode.noCandidates')}
              </p>
            ) : (
              <>
                <p className="text-h3 text-brand-900">
                  {t('studio:gis.batchGeocode.candidatesCount', { count: total })}
                </p>
                {data.sample.length > 0 && (
                  <div>
                    <p className="mb-1 text-h4 text-slate-700">
                      {t('studio:gis.batchGeocode.sample')}
                    </p>
                    <ul className="space-y-1 text-small text-slate-700">
                      {data.sample.slice(0, 8).map((c) => (
                        <li key={`${c.iri}-${c.entity_class}`} className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 text-slate-400" aria-hidden />
                          <span className="flex-1 truncate">{c.label}</span>
                          <Badge variant="neutral">
                            {c.entity_class.split(/[#/]/).pop()}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-small">
                    <span className="text-slate-700">
                      {t('studio:gis.batchGeocode.limit')}
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={batchLimit}
                      onChange={(e) => setBatchLimit(Number(e.target.value) || 0)}
                      className="h-9 w-full rounded border border-slate-300 px-2"
                    />
                  </label>
                  <label className="block text-small">
                    <span className="text-slate-700">
                      {t('studio:gis.batchGeocode.minConfidence')}
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      value={minConfidence}
                      onChange={(e) => setMinConfidence(Number(e.target.value) || 0)}
                      className="h-9 w-full rounded border border-slate-300 px-2"
                    />
                  </label>
                </div>

                <Button
                  onClick={() => {
                    setResult(null);
                    run.mutate();
                  }}
                  disabled={run.isPending || planned === 0}
                >
                  {run.isPending
                    ? t('studio:gis.batchGeocode.running', { n: planned, eta: etaSec })
                    : t('studio:gis.batchGeocode.run')}
                </Button>
              </>
            )}

            {result && (
              <div className="space-y-1 rounded border border-green-200 bg-green-50 px-3 py-2 text-small">
                <p className="text-green-800">
                  {t('studio:gis.batchGeocode.done', {
                    geocoded: result.geocoded,
                    written: result.written_triples,
                  })}
                </p>
                {(result.skipped_no_match > 0 || result.skipped_low_confidence > 0) && (
                  <p className="text-slate-600">
                    {t('studio:gis.batchGeocode.skipped', {
                      noMatch: result.skipped_no_match,
                      lowConf: result.skipped_low_confidence,
                    })}
                  </p>
                )}
                {result.failed > 0 && (
                  <p className="text-red-700">
                    {t('studio:gis.batchGeocode.failed', { count: result.failed })}
                  </p>
                )}
                <Button variant="ghost" size="sm" onClick={() => void refetch()}>
                  {t('common:actions.refresh')}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
